import { type ComponentProps, memo, useCallback } from 'react';

import { type OpenCreditPackModalLocations } from '@air/analytics';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { CreditUsageBreakdownCategoryList } from '~/components/CreditUsageMeter/CreditUsageBreakdownCategoryList';
import { CreditUsageBreakdownGiftCreditsRow } from '~/components/CreditUsageMeter/CreditUsageBreakdownGiftCreditsRow';
import { CreditUsageMeterBar } from '~/components/CreditUsageMeter/CreditUsageMeterBar';
import { CreditUsageMeterCount } from '~/components/CreditUsageMeter/CreditUsageMeterCount';
import { CreditUsageMeterResetDate } from '~/components/CreditUsageMeter/CreditUsageMeterResetDate';
import { CreditUsageOverageLabel } from '~/components/CreditUsageMeter/CreditUsageOverageLabel';
import { CreditUsageWarningBanner } from '~/components/CreditUsageMeter/CreditUsageWarningBanner';
import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { useCreditUsageBreakdown } from '~/components/CreditUsageMeter/useCreditUsageBreakdown';
import { ManageCreditPacksModal } from '~/components/Modals/ManageCreditPacks/ManageCreditPacksModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { isEligibleForCreditPacks } from '~/utils/creditUtils';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type CreditsMeterPopoverProps = Pick<ComponentProps<'div'>, 'className'> &
  Pick<CreditUsageStats, 'allocated' | 'used' | 'nextCycleStartAt'> & {
    allowCreditOverage: boolean;
    creditPackModalSource: Exclude<OpenCreditPackModalLocations, 'checkout'>;
    giftCreditsBalance: number;
  };

export const CreditsMeterPopover = memo(
  ({
    className,
    creditPackModalSource,
    giftCreditsBalance,
    allowCreditOverage,
    ...creditUsageStats
  }: CreditsMeterPopoverProps) => {
    const { allocated, used, nextCycleStartAt } = creditUsageStats;
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const { data: subscription } = useSubscriptionContext();
    const { data: creditStats } = useCreditStats();
    const [showManageCreditPacksModal] = useAirModal(ManageCreditPacksModal);

    const {
      handleSeePlansClick,
      hasAiAddons,
      isCreditModel,
      percentageUsed,
      showCategoryBreakdown,
      showPlansButton,
      usageState,
    } = useCreditUsageBreakdown({
      allocated,
      allowCreditOverage,
      giftCreditsBalance,
      used,
    });

    const showAddCreditsButton =
      canManageSubscription(permissions) && isEligibleForCreditPacks(subscription, creditStats);

    const handleAddCreditsClick = useCallback(() => {
      showManageCreditPacksModal({ source: creditPackModalSource });
    }, [creditPackModalSource, showManageCreditPacksModal]);

    return (
      <div className={cn('rounded-8 ads:bg-base bg-gray-1', className)} data-testid="CREDITS_METER_POPOVER">
        <CreditUsageWarningBanner percentageUsed={percentageUsed} usageState={usageState} />
        <div className="p-4">
          <div className="flex flex-col gap-2">
            <p className="font-semibold ads:text-primary text-10 text-gray-12 uppercase tracking-wider">
              Workspace Credit Usage
            </p>
            <CreditUsageMeterCount
              allocated={allocated}
              allowCreditOverage={allowCreditOverage}
              giftCreditsBalance={giftCreditsBalance}
              showGiftIndicator={false}
              used={used}
            />
            <CreditUsageOverageLabel allowCreditOverage={allowCreditOverage} />
            <CreditUsageMeterBar allocated={allocated} giftCreditsBalance={giftCreditsBalance} used={used} />
            {showCategoryBreakdown && (
              <CreditUsageBreakdownCategoryList hasAiAddons={hasAiAddons} isCreditModel={isCreditModel} used={used} />
            )}
            <CreditUsageBreakdownGiftCreditsRow giftCreditsBalance={giftCreditsBalance} />
          </div>

          <div className="flex items-center gap-2 pt-4 empty:hidden">
            {showPlansButton && (
              <Button
                appearance="filled"
                className="flex-1"
                color="blue"
                data-testid="CREDIT_USAGE_BREAKDOWN_UPGRADE_PLAN"
                onClick={handleSeePlansClick}
                size="small"
                type="button"
              >
                Upgrade plan
              </Button>
            )}
            {showAddCreditsButton && (
              <Button
                appearance="filled"
                className="flex-1"
                color="gray"
                data-testid="CREDIT_USAGE_BREAKDOWN_ADD_CREDITS"
                onClick={handleAddCreditsClick}
                size="small"
                type="button"
              >
                Add credits
              </Button>
            )}
          </div>
        </div>
        <div className="ads:border-default border-gray-4 border-t px-3 py-2">
          <CreditUsageMeterResetDate nextCycleStartAt={nextCycleStartAt} />
        </div>
      </div>
    );
  },
);

CreditsMeterPopover.displayName = 'CreditsMeterPopover';
